/**
 * AuditTrailTab — Inline audit trail for the entity detail panel
 *
 * Shows chronological log of every mutation for the selected entity.
 * Uses the backend auditTrail query with entity-scoped filtering.
 *
 * DUMB FRONTEND: All audit data comes from backend GraphQL.
 */

import { useState, useMemo } from 'react';
import {
  Stack,
  Text,
  Group,
  Badge,
  Box,
  Select,
  TextInput,
  Divider,
  ThemeIcon,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconFilter,
  IconShieldCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useTenantId } from '@/auth';
import type { AuditTrailEntry, AuditTrailResponse } from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// ============================================================================
// GRAPHQL QUERY
// ============================================================================

const AUDIT_TRAIL_QUERY = gql`
  query EntityAuditTrail($filter: AuditTrailFilter!) {
    getAuditTrail(filter: $filter) {
      entries {
        auditId
        action
        entityId
        entityType
        userId
        userName
        timestamp
        tamperDetected
        cryptographicHash
        severity
      }
      totalCount
      tamperAlerts {
        auditId
        reason
      }
    }
  }
`;

// ============================================================================
// HELPERS
// ============================================================================

function getActionIcon(action: string) {
  switch (action) {
    case 'CREATE':
      return IconPlus;
    case 'UPDATE':
      return IconEdit;
    case 'DELETE':
    case 'ARCHIVE':
      return IconTrash;
    default:
      return IconEdit;
  }
}

function getActionColor(action: string): string {
  switch (action) {
    case 'CREATE':
      return 'green';
    case 'UPDATE':
      return 'blue';
    case 'DELETE':
    case 'ARCHIVE':
      return 'red';
    case 'READ':
      return 'gray';
    default:
      return 'gray';
  }
}

function formatTimestamp(ts: string): string {
  try {
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function timeAgo(ts: string): string {
  try {
    const now = Date.now();
    const then = new Date(ts).getTime();
    const diff = now - then;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return '';
  }
}

// ============================================================================
// PROPS
// ============================================================================

interface AuditTrailTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function AuditTrailTab({ entity, primitiveType }: Readonly<AuditTrailTabProps>) {
  const tenantId = useTenantId() ?? '';
  const [filterAction, setFilterAction] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const entityId = entity?.id as string | undefined;

  const { data, loading, error } = useQuery<{ getAuditTrail: AuditTrailResponse }>(AUDIT_TRAIL_QUERY, {
    variables: {
      filter: {
        tenantId,
        entityId,
        entityType: primitiveType.toUpperCase(),
        pageSize: 50,
      },
    },
    skip: !entityId || !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const records: AuditTrailEntry[] = data?.getAuditTrail?.entries ?? [];
  const chainVerified = (data?.getAuditTrail?.tamperAlerts?.length ?? 0) === 0 && records.length > 0;
  const totalCount = data?.getAuditTrail?.totalCount ?? 0;

  // Filter records
  const filteredRecords = useMemo(() => {
    return records.filter((record: AuditTrailEntry) => {
      if (filterAction && record.action !== filterAction) return false;
      if (searchText) {
        const search = searchText.toLowerCase();
        return (
          record.action.toLowerCase().includes(search) ||
          record.userId.toLowerCase().includes(search) ||
          record.userName.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [records, filterAction, searchText]);

  // Unique actions for filter dropdown
  const uniqueActions = useMemo(
    () => [...new Set(records.map((r: AuditTrailEntry) => r.action))],
    [records]
  );

  if (!entityId) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select an entity to view its audit trail.</Text>
      </Center>
    );
  }

  if (loading && records.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">Loading audit trail...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Audit Trail Unavailable">
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {/* Chain integrity banner */}
      {records.length > 0 && (
        <Group gap="xs">
          <ThemeIcon
            size="xs"
            variant="light"
            color={chainVerified ? 'green' : 'red'}
          >
            {chainVerified ? <IconShieldCheck size={12} /> : <IconAlertTriangle size={12} />}
          </ThemeIcon>
          <Text size="xs" c={chainVerified ? 'green' : 'red'}>
            {chainVerified ? 'Chain integrity verified' : 'Chain integrity check failed'}
          </Text>
          <Text size="xs" c="dimmed">
            {totalCount} total event{totalCount !== 1 ? 's' : ''}
          </Text>
        </Group>
      )}

      {/* Filters */}
      <Group gap="xs">
        <TextInput
          placeholder="Search..."
          leftSection={<IconFilter size={14} />}
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
          size="xs"
          style={{ flex: 1 }}
        />
        <Select
          placeholder="Action"
          data={uniqueActions.map((a: string) => ({ value: a, label: a }))}
          value={filterAction}
          onChange={setFilterAction}
          clearable
          size="xs"
          w={140}
        />
      </Group>

      <Text size="xs" c="dimmed">
        Showing {filteredRecords.length} of {records.length} events
      </Text>

      <Divider />

      {/* Audit event list */}
      {filteredRecords.length === 0 ? (
        <Center py="lg">
          <Text size="sm" c="dimmed">
            {records.length === 0 ? 'No audit events recorded yet.' : 'No events match your filter.'}
          </Text>
        </Center>
      ) : (
        <Stack gap="xs">
          {filteredRecords.map((record: AuditTrailEntry) => (
            <AuditRecordItem key={record.auditId} record={record} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

// ============================================================================
// AUDIT RECORD ITEM
// ============================================================================

function AuditRecordItem({ record }: Readonly<{ record: AuditTrailEntry }>) {
  const RecordIcon = getActionIcon(record.action);
  const actionColor = getActionColor(record.action);

  return (
    <Box>
      <Group gap="xs" mb={4}>
        <ThemeIcon color={actionColor} size={24} variant="light">
          <RecordIcon size={12} />
        </ThemeIcon>
        <Stack gap={0} style={{ flex: 1 }}>
          <Group gap="xs">
            <Badge size="xs" variant="light" color={actionColor}>
              {record.action}
            </Badge>
            {record.tamperDetected && (
              <Badge size="xs" variant="light" color="red">TAMPERED</Badge>
            )}
          </Group>
          <Group gap="xs" mt={2}>
            <Text size="xs" c="dimmed">
              {record.userName || record.userId}
            </Text>
            <Text size="xs" c="dimmed">
              {timeAgo(record.timestamp)}
            </Text>
          </Group>
        </Stack>
      </Group>
      <Divider />
    </Box>
  );
}
